/**
 * The nav bar follows the VIEWPORT, not the last click.
 *
 * Click-only state is the bug this exists to remove: tap Board, scroll on to
 * Gear, and the bar still says Board — it reports where you last pressed
 * rather than where you are, which is the one thing a nav must not do. Both
 * bespoke hub navs (RTTR, Full Throttle) solved this with their own observer;
 * the spine had none, so every surface built on it inherited the defect.
 *
 * Nav id and slot id are not the same string (a block's entry is `band-<id>`
 * against a slot of `<id>`), so callers pass the pairing rather than this
 * guessing at it.
 */
import {useEffect} from "react";

export interface SpySlot {
    /** What the nav entry calls itself — the value written to active state. */
    navId: string;
    /** The slot rendered on the page, found as `#slot-<id>` or `[data-slot-id]`. */
    slotId: string;
}

/**
 * The fixed header's height. Scroll-to and scroll-spy must agree on it: if
 * the spy's band started above where `scrollToSlot` parks a section, arriving
 * at a section could mark the one before it. Single source, imported by both.
 */
export const HEADER_ALLOWANCE = 92;
/* Below this fraction of the viewport a slot is still arriving, not arrived. */
const FOLD_BAND = "55%";

/**
 * Marks the entry whose slot is highest in the reading band — the upper-middle
 * of the screen, so a section counts as "here" once it reaches reading position
 * rather than the instant its top edge appears.
 */
export function useSlotScrollSpy(
    slots: SpySlot[],
    setActive: (navId: string) => void,
    enabled = true,
): void {
    /* The pairing is rebuilt each render; its identity is not a dependency. */
    const key = slots.map(s => `${s.navId}:${s.slotId}`).join("|");

    useEffect(() => {
        if (!enabled || !slots.length || typeof IntersectionObserver === "undefined") return;

        const byElement = new Map<Element, string>();
        for (const {navId, slotId} of slots) {
            const el = document.getElementById(`slot-${slotId}`) ??
                document.querySelector(`[data-slot-id="${slotId}"]`);
            if (el) byElement.set(el, navId);
        }
        if (!byElement.size) return;

        /*
         * Keep every visible slot, then take the topmost. Setting the active
         * entry from whichever callback fired last picks an arbitrary one when
         * two slots share the band — which reads as the bar flickering between
         * neighbours as you scroll.
         */
        const visible = new Set<Element>();
        const observer = new IntersectionObserver(entries => {
            for (const entry of entries) {
                if (entry.isIntersecting) visible.add(entry.target);
                else visible.delete(entry.target);
            }
            if (!visible.size) return;
            const top = [...visible].sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top)[0];
            const navId = byElement.get(top);
            if (navId) setActive(navId);
        }, {rootMargin: `-${HEADER_ALLOWANCE}px 0px -${FOLD_BAND} 0px`, threshold: 0});

        byElement.forEach((_, el) => observer.observe(el));
        return () => observer.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, enabled, setActive]);
}
